import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SupportPageService } from 'app/core/services/support-page.service';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { AppConstants } from 'app/app.constants';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import {
  FileDataCategories,
  FileMetaData,
  ProductFile,
} from 'app/shared/interfaces/upload-file-support-page';
import { ToastrService } from 'ngx-toastr';
import { forkJoinOrEmpty } from 'app/utils/rxjs.utils';

type EntryDeletionResult = {
  category: string;
  file: FileMetaData;
  failed: boolean;
};

export interface Product {
  id: number;
  name: string;
  photo: string;
}

@Component({
  standalone: false,
  selector: 'app-form-edit-product',
  templateUrl: './form-edit-product.component.html',
  styleUrls: ['./form-edit-product.component.scss'],
})
export class FormEditProductComponent implements OnInit {
  editFormProduct: FormGroup;
  imageSrc: string;
  products = [];
  isNewProduct: boolean = false;
  selectedProduct: boolean = false;
  selectedProductInfo: {
    label: string;
    value: string;
    category: string;
    imageSrc: string;
  };
  defaultImageSrc =
    './../../../../assets/images/support-page/products/default-product-tecomec.svg';
  origFileName: string;
  file: any;
  isValidFileType = false;
  categories: { key: string; value: string }[] = [];
  showConfirmationModal: boolean;
  confirmationModalMessage: string;
  modalTitleConfirmation: string;
  confirmationModalType: string;
  modalTranslateButtonConfirmText: string;
  modalTranslateButtonCancelText: string;
  shouldDeleteDoc: boolean;

  constructor(
    private readonly fb: FormBuilder,
    private readonly supportPageService: SupportPageService,
    private readonly dialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly toastr: ToastrService,
  ) {
    this.editFormProduct = this.fb.group({
      productName: ['', Validators.required],
      productCategory: ['', Validators.required],
      productSelect: [''],
      productImage: [''],
    });
  }

  ngOnInit(): void {
    this.supportPageService.getAllSupportFile().subscribe(
      (supportFiles: any) => {
        if (!supportFiles || supportFiles.length === 0) {
          return;
        }
        this.products = supportFiles
          .filter(
            (file: {
              id: string;
              productName: string;
              productCategory: string;
            }) => file.productName && file.id !== 'categories',
          )
          .map((file) => ({
            value: file.id,
            label: file.productName,
            category: file.productCategory,
          }));
      },
      (error) => {
        console.error(error);
      },
    );

    this.supportPageService
      .getSupportFileById('categories')
      .subscribe((data: any) => {
        if (!data || !data.categories) {
          return;
        }
        this.categories = [];

        const currentLanguage = localStorage.getItem(
          AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG,
        );

        for (const key in data.categories) {
          if (data.categories[key][currentLanguage]) {
            const categories = {
              key: key,
              value: data.categories[key][currentLanguage],
            };
            this.categories.push(categories);
          }
        }
      });
  }

  onSubmit() {}

  previewImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file && file.type === 'image/png') {
      this.isValidFileType = true;
      this.origFileName = file.name;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageSrc = e.target?.result as string;
      };
      reader.readAsDataURL(file);
      this.file = file;
    } else {
      this.isValidFileType = false;
    }
  }

  private fetchProductImg(productName: string) {
    const formatedProductName = this.toSnakeCase(productName);
    const url = [`images/${formatedProductName}.png`];
    this.supportPageService.getPresignedUrl(url).subscribe((data) => {
      if (data) {
        this.imageSrc = data[0];
      } else {
        this.imageSrc = this.defaultImageSrc;
      }
      this.selectedProductInfo.imageSrc = this.imageSrc;
    });
  }

  toSnakeCase(input: string): string {
    return input.trim().toLowerCase().replace(/\s+/g, '_');
  }

  selectProduct(productName: string): void {
    const product = this.products.find(
      (product) => product.label === productName,
    );
    this.selectedProductInfo = product;
    this.editFormProduct
      .get('productName')
      ?.setValue(this.selectedProductInfo.label);
    this.editFormProduct
      .get('productCategory')
      ?.setValue(this.selectedProductInfo.category);
    this.fetchProductImg(this.selectedProductInfo.value);
    this.selectedProduct = true;
    this.isNewProduct = false;
    this.isValidFileType = true;
    this.editFormProduct.markAsPristine();
  }

  addNewProduct(): void {
    this.editFormProduct.reset();
    this.editFormProduct.get('productSelect')?.setValue('');
    this.selectedProduct = false;
    this.isNewProduct = true;
    this.isValidFileType = false;
    this.imageSrc = this.defaultImageSrc;
    this.selectedProductInfo = null;
  }

  /**
   * Deletes every blob owned by a version entry.
   *
   * @remarks
   * Multi-language entries keep one blob per language in `files`, deleted by
   * their stored path as-is: its category prefix may predate a product category
   * change. Legacy entries predate `files` and only own the single `fileName`
   * blob, whose path is rebuilt from the entry metadata.
   */
  private deleteEntryBlobs(
    data: ProductFile,
    fileCategory: string,
    file: FileMetaData,
  ): Observable<unknown> {
    const languageFiles = file.files ?? [];

    if (languageFiles.length === 0) {
      return this.supportPageService.deleteProductFileFromBlob(
        data.id,
        data.productCategory,
        file.fileName,
        fileCategory,
        file.fileVersion,
        file.fileDate,
      );
    }

    return forkJoin(
      languageFiles.map((languageFile) =>
        this.supportPageService.deleteSupportBlob(
          languageFile.filePathBlobStorage,
        ),
      ),
    );
  }

  /**
   * Deletes the files of a product, and the product itself when requested.
   *
   * @remarks
   * Blobs go first and Cosmos is written once at the end, never per entry: the
   * backend removes an entry with an unguarded read-filter-upsert, so parallel
   * per-entry removals overwrite each other and bring entries back.
   * - Every blob deleted and `shouldDeleteDoc`: the product image, then the
   *   product document.
   * - Otherwise: the document keeps only the entries whose blobs failed, so the
   *   deletion can be retried. The product survives any failure.
   */
  private deleteAllFilesFromBlobAndCosmo(
    data: ProductFile,
    shouldDeleteDoc: boolean,
  ) {
    const entryDeletions: Observable<EntryDeletionResult>[] = [];

    for (const category of Object.keys(data.fileDataCategories ?? {})) {
      const files: FileMetaData[] = data.fileDataCategories[category] ?? [];
      for (const file of files) {
        entryDeletions.push(
          this.deleteEntryBlobs(data, category, file).pipe(
            map(() => ({ category, file, failed: false })),
            catchError(() => of({ category, file, failed: true })),
          ),
        );
      }
    }

    forkJoinOrEmpty(entryDeletions)
      .pipe(
        concatMap((results) =>
          this.persistProductDeletion(data, shouldDeleteDoc, results),
        ),
      )
      .subscribe(this.handleFinalResult.bind(this));
  }

  private persistProductDeletion(
    data: ProductFile,
    shouldDeleteDoc: boolean,
    results: EntryDeletionResult[],
  ): Observable<{ hasError: boolean }> {
    const failedEntries = results.filter((result) => result.failed);
    const hasErrorInFiles = failedEntries.length > 0;

    if (shouldDeleteDoc && !hasErrorInFiles) {
      return this.supportPageService
        .deleteProductFileFromBlob('', 'images', `${data.id}.png`)
        .pipe(
          concatMap(() =>
            this.supportPageService.deleteProductFromCosmo(data.id),
          ),
          map(() => ({ hasError: false })),
          // The file blobs are gone, so their entries must go even if the
          // product itself could not be deleted.
          catchError(() => this.keepEntries(data, [], true)),
        );
    }

    if (results.length === 0) {
      return of({ hasError: false });
    }

    return this.keepEntries(data, failedEntries, hasErrorInFiles);
  }

  /** Rewrites the product with only the given entries left. */
  private keepEntries(
    data: ProductFile,
    entries: EntryDeletionResult[],
    hasError: boolean,
  ): Observable<{ hasError: boolean }> {
    const remaining: { [category: string]: FileMetaData[] } = {};
    for (const { category, file } of entries) {
      remaining[category] = [...(remaining[category] ?? []), file];
    }

    return this.supportPageService
      .updateSupportFile(data.id, {
        ...data,
        fileDataCategories: remaining as FileDataCategories,
      })
      .pipe(
        map(() => ({ hasError })),
        catchError(() => of({ hasError: true })),
      );
  }

  private handleFinalResult({ hasError }: { hasError: boolean }) {
    const key = hasError
      ? 'download-support-page.error-delete-file'
      : 'download-support-page.file-successfully-deleted';

    this.translateService.get(key).subscribe((res: string) => {
      hasError
        ? this.toastr.error(res, '', {
            positionClass: 'toast-top-full-width',
            disableTimeOut: false,
          })
        : this.toastr.info(res, '', {
            positionClass: 'toast-top-full-width',
            disableTimeOut: false,
          });
    });

    setTimeout(() => {
      this.dialog.closeAll();
    }, TimeoutEnum.Short);
  }

  translateConfirmationMessage() {
    this.translateService
      .get('global.alert.delete-title')
      .subscribe((res: string) => {
        this.showConfirmationModal = true;
        this.confirmationModalMessage = '';
        this.modalTitleConfirmation = res;
        this.confirmationModalType = 'warning';
        this.modalTranslateButtonConfirmText = 'global.delete';
        this.modalTranslateButtonCancelText = 'global.cancel';
      });
  }

  onButtonAction(event: {
    action: boolean;
    type: string;
    buttonText?: string;
  }) {
    if (event.buttonText === 'Excluir' || event.buttonText === 'Delete') {
      this.deleteProduct(this.shouldDeleteDoc);
    }
  }

  openModalToDeleteFile(shouldDeleteDoc: boolean) {
    this.shouldDeleteDoc = shouldDeleteDoc;
    this.translateConfirmationMessage();
  }

  deleteProduct(shouldDeleteDoc: boolean): void {
    if (this.selectedProductInfo?.value) {
      this.supportPageService
        .getSupportFileById(this.selectedProductInfo.value)
        .subscribe((supportFile) => {
          this.deleteAllFilesFromBlobAndCosmo(
            supportFile as ProductFile,
            shouldDeleteDoc,
          );
        });
    }
  }

  editProduct(): void {
    const productName = this.editFormProduct.value.productName;
    const productCategory = this.editFormProduct.value.productCategory;
    const productId = this.selectedProductInfo.value;

    const hasChanges =
      productName !== this.selectedProductInfo.label ||
      productCategory !== this.selectedProductInfo.category ||
      this.imageSrc !== this.selectedProductInfo.imageSrc;

    if (!hasChanges) return;

    this.supportPageService
      .getSupportFileById(productId)
      .subscribe((supportFile) => {
        if ('fileDataCategories' in supportFile) {
          const updatedData = {
            id: productId,
            productName: productName,
            productCategory: productCategory,
            fileDataCategories: supportFile.fileDataCategories,
            imageUrl: supportFile.imageUrl
          };

          this.supportPageService
            .updateSupportFile(productId, updatedData)
            .subscribe(
              () => {
                if (this.imageSrc !== this.selectedProductInfo.imageSrc) {
                  const formattedFileName = `${productId}.png`;
                  this.addFileToBlobStorage(formattedFileName, this.file);
                } else {
                  this.translateService
                    .get('global.alert.success-text')
                    .subscribe((msg) =>
                      this.toastr.success(msg, '', {
                        positionClass: 'toast-top-full-width',
                        disableTimeOut: false,
                      }),
                    );
                  setTimeout(() => {
                    this.dialog.closeAll();
                  }, TimeoutEnum.Short);
                }
              },
              (error) => {
                this.handlePermissionError(error);
              },
            );
        }
      });
  }

  saveProduct(): void {
    const productName = this.editFormProduct.value.productName;
    const category = this.editFormProduct.value.productCategory;
    const checkIfProductExist = this.products.some((product) => {
      return product.label === productName;
    });

    if (checkIfProductExist) {
      this.translateService
        .get('global.support-duplicate-file-msg')
        .subscribe((msg) =>
          this.toastr.error(msg, '', {
            positionClass: 'toast-top-full-width',
            disableTimeOut: false,
          }),
        );
      setTimeout(() => {}, TimeoutEnum.Short);
      return;
    }

    const updatedData = {
      id: this.toSnakeCase(productName),
      productName: productName,
      productCategory: category,
      fileDataCategories: {},
      imageUrl: ''
    };

    this.supportPageService.postSupportFile(updatedData).subscribe(
      () => {
        const formatedFileName = `${this.toSnakeCase(productName)}.png`;
        this.addFileToBlobStorage(formatedFileName, this.file);
      },
      () => {
        this.translateService
          .get('global.support-error-save-file-in-db')
          .subscribe((msg) =>
            this.toastr.error(msg, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            }),
          );
      },
    );
  }

  closeDialog(): void {
    const dialogRef = this.dialog.closeAll();
  }

  private addFileToBlobStorage(newFileName: string, file: any) {
    const fileName = [`images/${newFileName}`];
    this.supportPageService.getPresignedUrl(fileName).subscribe(
      (data) => {
        this.supportPageService.uploadFile(file, data[0]).subscribe(
          () => {
            this.translateService
              .get('global.alert.send-file-upload-success')
              .subscribe((msg) =>
                this.toastr.success(msg, '', {
                  positionClass: 'toast-top-full-width',
                  disableTimeOut: false,
                }),
              );
            setTimeout(() => {
              this.dialog.closeAll();
            }, TimeoutEnum.Short);
          },
          (error) => {
            this.handlePermissionError(error);
          },
        );
      },
      (error) => {
        console.error('Erro ao obter URL pré-assinada:', error);
        this.handlePermissionError(error);
      },
    );
  }

  private handlePermissionError(error: any) {
    this.translateService
      .get('global.validate.error-permission')
      .subscribe((permissionMsg: string) => {
        const errorMessage =
          error.error?.text === permissionMsg ? permissionMsg : 'error';
        this.handleErrorAlert(errorMessage);
      });
  }

  private handleErrorAlert(translationKey: string) {
    this.translateService.get(translationKey).subscribe((msg) =>
      this.toastr.error(msg, '', {
        positionClass: 'toast-top-full-width',
        disableTimeOut: false,
      }),
    );
  }
}
